import React, { useState, useEffect } from 'react';
import { TicketCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackTripCardAction } from '@/utils/analytics';
import Hey from '@/lib/hey-utils';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ETicketButtonProps {
  confirmationNumbers?: {
    ticket: string;
    isDownloadable: boolean;
  }[];
  flightNumber: string;
  tripId: string;
  destinationName: string;
  className?: string;
}

// Cache for document URLs
const documentUrlCache: Record<string, string> = {};

const ETicketButton: React.FC<ETicketButtonProps> = ({
  confirmationNumbers = [],
  flightNumber,
  tripId,
  destinationName,
  className = ''
}) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<string | null>(null);
  const [expandTicket, setExpandTicket] = useState(false);
  
  // Always show the button if there are any confirmation numbers
  const hasConfirmationNumbers = confirmationNumbers && confirmationNumbers.length > 0;
  
  // Check if there's at least one downloadable ticket
  const hasDownloadableTicket = hasConfirmationNumbers && 
    confirmationNumbers.some(cn => cn && cn.isDownloadable);
  
  // Pre-fetch the document URL when the component mounts
  useEffect(() => {
    const prefetchDocumentUrl = async () => {
      if (hasDownloadableTicket) {
        try {
          // Find the downloadable ticket
          const downloadableTicket = confirmationNumbers.find(cn => cn.isDownloadable);
          if (!downloadableTicket) return;
          
          const ticketNumber = downloadableTicket.ticket;
          
          // Check if we already have this URL in the cache
          const cacheKey = `${tripId}_${ticketNumber}`;
          if (documentUrlCache[cacheKey]) {
            console.debug('Using cached document URL for:', ticketNumber);
            setDocumentUrl(documentUrlCache[cacheKey]);
            return;
          }
          
          // Get the current user ID
          const heyUtils = Hey.HeyUtils.getInstance();
          const userId = heyUtils.user_id;
          
          console.debug('Pre-fetching document URL for ticket:', ticketNumber);
          
          // Call the API to get the document URL
          const response = await heyUtils.getTicketDocumentReceipt(
            userId,
            tripId,
            ticketNumber
          );
          
          if (response && response.documentUrl) {
            console.debug('Pre-fetched document URL:', response.documentUrl);
            
            // Store in the cache
            documentUrlCache[cacheKey] = response.documentUrl;
            setDocumentUrl(response.documentUrl);
            
            // Pre-load the URL by creating a hidden iframe
            try {
              const iframe = document.createElement('iframe');
              iframe.style.display = 'none';
              iframe.src = response.documentUrl;
              document.body.appendChild(iframe);
              
              // Remove the iframe after a short delay
              setTimeout(() => {
                document.body.removeChild(iframe);
              }, 1000);
            } catch (preloadError) {
              console.error('Error pre-loading document URL:', preloadError);
            }
          }
        } catch (error) {
          console.error('Error pre-fetching document URL:', error);
        }
      }
    };
    
    prefetchDocumentUrl();
  }, [hasDownloadableTicket, confirmationNumbers, tripId]);

  useEffect(() => {
    // Listen for events from other buttons
    const handleOtherButtonExpanded = () => {
      setExpandTicket(false);
    };
    
    // Add event listeners
    document.addEventListener('checkInExpanded', handleOtherButtonExpanded);
    document.addEventListener('statusExpanded', handleOtherButtonExpanded);
    
    // Clean up
    return () => {
      document.removeEventListener('checkInExpanded', handleOtherButtonExpanded);
      document.removeEventListener('statusExpanded', handleOtherButtonExpanded);
    };
  }, []);

  // Function to open PDF in PDF viewer mode
  const openPdfInViewer = (url: string) => {
    // For Chrome and Firefox, adding #view=FitH to the URL forces PDF viewer mode
    const pdfViewerUrl = `${url}#view=FitH`;
    
    // Open in a new tab
    const newWindow = window.open(pdfViewerUrl, '_blank');
    
    // If popup was blocked, try to focus it
    if (newWindow) {
      newWindow.focus();
    }
  };
  
  const handleETicketClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    console.debug(`🖱️ E-Ticket button clicked for flight ${flightNumber}`);
    trackTripCardAction("e_ticket", tripId, destinationName);
    
    // If we have confirmation numbers but none are downloadable, show the dialog
    if (hasConfirmationNumbers && !hasDownloadableTicket) {
      // Find the first ticket number
      const firstTicket = confirmationNumbers[0]?.ticket;
      if (firstTicket) {
        setSelectedTicket(firstTicket);
        setShowTicketDialog(true);
      } else {
        toast({
          title: t("tripCard.eTicket.notAvailable", "E-Ticket Not Available"),
          description: t("tripCard.eTicket.noTicketNumber", "No ticket number found."),
          variant: "destructive"
        });
      }
      return;
    }
    
    if (hasDownloadableTicket) {
      try {
        setIsLoading(true);
        
        // If we already have the document URL from pre-fetching, use it
        if (documentUrl) {
          console.debug('Using pre-fetched document URL:', documentUrl);
          openPdfInViewer(documentUrl);
          
          toast({
            title: t("tripCard.eTicket.success", "E-Ticket Opening"),
            variant: "default",
            duration: 3000,
          });
          return;
        }
        
        // Otherwise, fetch it now
        // Find the downloadable ticket
        const downloadableTicket = confirmationNumbers.find(cn => cn.isDownloadable);
        if (!downloadableTicket) {
          throw new Error('No downloadable ticket found');
        }
        
        const ticketNumber = downloadableTicket.ticket;
        console.debug(`🎫 Getting E-Ticket document for: ${ticketNumber}`);
        
        // Get the current user ID
        const heyUtils = Hey.HeyUtils.getInstance();
        const userId = heyUtils.user_id;
        
        console.debug('User ID:', userId);
        console.debug('Trip ID:', tripId);
        console.debug('Ticket Number:', ticketNumber);
        
        // Call the API to get the document URL
        const response = await heyUtils.getTicketDocumentReceipt(
          userId,
          tripId,
          ticketNumber
        );
        
        if (!response || !response.documentUrl) {
          throw new Error('No document URL returned from the API');
        }
        
        console.debug('Document URL received:', response.documentUrl);
        
        // Store in the cache for future use
        const cacheKey = `${tripId}_${ticketNumber}`;
        documentUrlCache[cacheKey] = response.documentUrl;
        
        // Open the PDF in viewer mode
        openPdfInViewer(response.documentUrl);
        
        // Show a simple success toast
        toast({
          title: t("tripCard.eTicket.success", "E-Ticket Opening"),
          variant: "default",
          duration: 3000,
        });
      } catch (error) {
        console.error('Error getting E-Ticket:', error);
        toast({
          title: t("tripCard.eTicket.error", "E-Ticket Failed"),
          description: t("tripCard.eTicket.errorDescription", "Failed to get E-Ticket. Please try again later."),
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      console.debug('No downloadable ticket available');
      toast({
        title: t("tripCard.eTicket.notAvailable", "E-Ticket Not Available"),
        description: t("tripCard.eTicket.notAvailableDescription", "This E-Ticket is not downloadable at this time."),
        variant: "destructive"
      });
    }
  };

  const handleTicketMouseEnter = () => {
    setExpandTicket(true);
    // Dispatch event to notify other buttons
    document.dispatchEvent(new CustomEvent('eTicketExpanded'));
    
    // Auto-hide after 0.5 seconds
    setTimeout(() => {
      setExpandTicket(false);
    }, 500);
  };
  
  const handleTicketMouseLeave = () => {
    setExpandTicket(false);
  };

  // Debug logging
  console.debug('ETicketButton rendering with:', { 
    confirmationNumbers, 
    hasConfirmationNumbers, 
    hasDownloadableTicket,
    tripId,
    documentUrl
  });

  // Always render the button when there are confirmation numbers
  if (!hasConfirmationNumbers) {
    return null;
  }
  
  return (
    <>
      <Button 
        size="sm" 
        variant="default" 
        className={`bg-teal-600 hover:bg-teal-700 text-white text-xs py-1 ${isMobile && expandTicket ? 'px-2' : 'px-1'} h-7 rounded shadow-sm flex items-center gap-1 ${className}`}
        onClick={handleETicketClick}
        disabled={isLoading}
        onMouseEnter={handleTicketMouseEnter}
        onMouseLeave={handleTicketMouseLeave}
        onTouchStart={handleTicketMouseEnter}
      >
        {isLoading ? (
          <span className="animate-spin h-4 w-4 mr-1 border-2 border-white border-t-transparent rounded-full" />
        ) : (
          isMobile ? (
            <>
              <TicketCheck className="w-5 h-5" />
              {expandTicket && <span className="ml-1">E-Ticket</span>}
            </>
          ) : (
            <>
              <TicketCheck className="w-5 h-5 mr-1" />
              E-Ticket
            </>
          )
        )}
      </Button>
      
      {/* Dialog to display E-Ticket number when not downloadable */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("tripCard.eTicket.ticketDetails", "E-Ticket Details")}</DialogTitle>
            <DialogDescription>
              {t("tripCard.eTicket.ticketDetailsDescription", "Your E-Ticket information for this flight.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <h4 className="font-medium text-sm text-gray-500">{t("tripCard.eTicket.ticketNumber", "E-Ticket Number")}</h4>
              <div className="bg-gray-100 p-3 rounded-md font-mono text-lg break-all">
                {selectedTicket}
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {t("tripCard.eTicket.ticketNotDownloadable", "This E-Ticket is not available for download. Please contact the airline directly if you need a copy of your E-Ticket.")}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ETicketButton;
